import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, History, ListOrdered } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsPhone } from '@/hooks/useIsPhone'
import type { OrderData } from '../../bot/types/chartData'
import { buildWalkthroughTrades, type WalkthroughTrade } from './walkthroughTrades'

const MIN_HEIGHT = 120
const DEFAULT_HEIGHT = 132
/** Never a full-screen table: this is a validation receipt, not the Backtest tab. */
const MAX_HEIGHT_FRACTION = 0.4
/**
 * A phone gives the pane less, because the chart is the thing being watched and the phone's
 * transport band now takes a row of its own out of the same column. At 0.3 the chart still
 * holds above ~55% of the viewport with the pane dragged to its largest, and around ~72% at
 * the pane's default (which on a phone is its minimum).
 */
const PHONE_MAX_HEIGHT_FRACTION = 0.3

const COLUMNS = [
  'Ticket',
  'Type',
  'Entry',
  'Stop loss',
  'Take profit',
  'Exit',
  'P/L (pips)',
  'Time',
] as const

type PaneTab = 'open' | 'closed'

export interface WalkthroughTradesPaneProps {
  /**
   * Every order event of the run that is on the chart — see useWalkthroughOrders, which owns
   * both the fetch and the "is this position in view at all?" question.
   */
  orders: OrderData[]
  /** Replay cursor, unix seconds. Rows appear as it passes them. */
  playheadTs: number
  /** Last revealed close, for floating pips on open trades. */
  markPrice: number | null
  /** From backtest_config.pip_size. */
  pipSize: number
  /** Price decimals, from the symbol's point size. */
  precision: number
  /**
   * Bring a trade into view on the chart. Rows are inert without it.
   *
   * Takes the trade's ENTRY time, and the handler behind it only scrolls — it must not move the
   * playhead. The replay keeps its place: looking at a trade is not the same act as winding the
   * run back to it. And reveal is what decides whether a trade reads as open or closed, so
   * rewinding to a closed trade's entry would reopen it and move the very row that was clicked
   * onto the other tab.
   */
  onShowTrade?: (entryTs: number) => void
  /**
   * The playhead has reached the end of the run.
   *
   * Lands the pane on Closed trades, once, on the edge — "no open trades right now" is the
   * wrong thing to be looking at when the run has finished. Once only, so a tab the user picks
   * afterwards is not dragged back.
   */
  atEnd?: boolean
  className?: string
}

function formatPips(v: number | null): string {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : v < 0 ? '−' : ''
  return `${sign}${Math.abs(v).toFixed(1)}`
}

/**
 * A trade's moment, short.
 *
 * Not `formatDateLabel` + `formatTimeLabel` from chartUtils: those are two calls and the first
 * carries the year, which is noise on a run whose trades are days apart at most.
 */
function formatTime(ts: number | null): string {
  if (ts == null) return '—'
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function TradeRows({
  rows,
  precision,
  onShowTrade,
  selectedTicket,
  onSelect,
}: {
  rows: WalkthroughTrade[]
  precision: number
  onShowTrade?: (entryTs: number) => void
  selectedTicket: number | null
  onSelect: (ticket: number) => void
}) {
  const price = (v: number | null) => (v == null ? '—' : v.toFixed(precision))

  const show = (t: WalkthroughTrade) => {
    onSelect(t.ticket)
    onShowTrade?.(t.entryTs)
  }

  return (
    <>
      {rows.map((t) => (
        <tr
          key={`${t.ticket}:${t.entryTs}`}
          onClick={onShowTrade ? () => show(t) : undefined}
          onKeyDown={
            onShowTrade
              ? (e) => {
                  // A <tr> is not a button, so the pointer affordance has to be matched by hand
                  // rather than inherited. Cheap here, and the shared TradesTable's click-only
                  // row is not a gap worth copying into new code.
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()
                  show(t)
                }
              : undefined
          }
          tabIndex={onShowTrade ? 0 : undefined}
          aria-label={onShowTrade ? `Show trade #${t.ticket} on the chart` : undefined}
          className={cn(
            'border-b border-border last:border-b-0',
            onShowTrade && 'cursor-pointer hover:bg-muted/50',
            // The same fill as the active tab pill: one "this is the one" language in the pane.
            selectedTicket === t.ticket && 'bg-muted',
          )}
        >
          <td className="px-3 py-2 text-caption tabular-nums text-muted-foreground whitespace-nowrap">
            #{t.ticket}
          </td>
          <td
            className={cn(
              'px-3 py-2 text-caption font-semibold',
              t.type === 'Buy' ? 'text-success' : 'text-danger',
            )}
          >
            {t.type}
          </td>
          <td className="px-3 py-2 text-body tabular-nums">{price(t.entry)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(t.sl)}</td>
          <td className="px-3 py-2 text-body tabular-nums text-muted-foreground">{price(t.tp)}</td>
          <td className="px-3 py-2 text-body tabular-nums">{price(t.exit)}</td>
          <td
            className={cn(
              'px-3 py-2 text-body tabular-nums',
              t.pips == null
                ? 'text-muted-foreground'
                : t.pips >= 0
                  ? 'text-success'
                  : 'text-danger',
            )}
          >
            {formatPips(t.pips)}
          </td>
          {/* The ENTRY moment on both tabs, so the column means one thing throughout — when the
              trade was taken — and agrees with where a row click lands. */}
          <td className="px-3 py-2 text-caption text-muted-foreground whitespace-nowrap">
            {formatTime(t.entryTs)}
          </td>
        </tr>
      ))}
    </>
  )
}

/**
 * The walkthrough's trade list: what the generated code is holding, and what it has closed,
 * as of the replay playhead.
 *
 * Pips only, no money, no totals — see walkthroughTrades.ts for why. Fixed-ceiling height so
 * it can never grow into a backtest-sized table.
 */
export default function WalkthroughTradesPane({
  orders,
  playheadTs,
  markPrice,
  pipSize,
  precision,
  onShowTrade,
  atEnd = false,
  className,
}: WalkthroughTradesPaneProps) {
  const isPhone = useIsPhone()
  const [collapsed, setCollapsed] = useState(false)
  const [tab, setTab] = useState<PaneTab>('open')
  const [height, setHeight] = useState(isPhone ? MIN_HEIGHT : DEFAULT_HEIGHT)
  /**
   * The trade last pointed at on the chart, by TICKET.
   *
   * A ticket rather than a list index: it is stable across the open→closed transition, so the
   * highlight follows a trade onto the other tab — and it self-clears, because a scrub back that
   * un-reveals the trade simply leaves no row matching it. Nothing has to remember to reset it.
   */
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)

  const maxHeight = () =>
    Math.round(window.innerHeight * (isPhone ? PHONE_MAX_HEIGHT_FRACTION : MAX_HEIGHT_FRACTION))

  // Rotating a tablet into phone width, or resizing a desktop window down, can leave a height
  // that is now most of the viewport. Clamp on the crossover rather than waiting for the next
  // drag to notice.
  useEffect(() => {
    if (!isPhone) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciling a stored size against a breakpoint change IS the synchronization
    setHeight((h) => Math.min(h, maxHeight()))
    // maxHeight closes over isPhone, which is the only thing that changes it here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhone])

  // Land on Closed when the run finishes. On the false→true EDGE only: fired on every render
  // while `atEnd` held it would keep pulling the user off a tab they had chosen afterwards, and
  // the point is only to not leave them staring at "no open trades right now" at the finish.
  const prevAtEndRef = useRef(atEnd)
  useEffect(() => {
    const wasAtEnd = prevAtEndRef.current
    prevAtEndRef.current = atEnd
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a one-shot edge signal: moving the tab IS the effect's purpose
    if (atEnd && !wasAtEnd) setTab('closed')
  }, [atEnd])

  const { open, closed } = useMemo(
    () => buildWalkthroughTrades(orders, playheadTs, pipSize, markPrice),
    [orders, playheadTs, pipSize, markPrice],
  )

  const onResizeDown = (e: React.PointerEvent) => {
    e.preventDefault()
    dragRef.current = { startY: e.clientY, startH: height }
    const max = maxHeight()
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      setHeight(Math.min(max, Math.max(MIN_HEIGHT, d.startH + (d.startY - ev.clientY))))
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const tabs: Array<{ id: PaneTab; label: string; Icon: typeof ListOrdered; count: number }> = [
    { id: 'open', label: 'Open trades', Icon: ListOrdered, count: open.length },
    { id: 'closed', label: 'Closed trades', Icon: History, count: closed.length },
  ]
  const rows = tab === 'open' ? open : closed

  return (
    <div className={cn('shrink-0 flex flex-col bg-card', className)}>
      {/* A hairline to look at, with a ±5px invisible grab area hanging off it: a 1px target is
          not a thing a pointer can catch, and a 4px visible bar reads as chrome the pane does not
          need. Not the shared ResizeHandle — that one is mouse-event-only, so adopting it would
          lose the touch drag this pane was tuned for. */}
      {!collapsed && (
        <div
          role="separator"
          aria-orientation="horizontal"
          onPointerDown={onResizeDown}
          onDoubleClick={() => setHeight(isPhone ? MIN_HEIGHT : DEFAULT_HEIGHT)}
          className="relative shrink-0 h-px w-full cursor-row-resize touch-none bg-border
                     hover:bg-primary/40 transition-colors
                     before:absolute before:left-0 before:right-0 before:-top-[5px]
                     before:-bottom-[5px] before:content-['']"
        />
      )}

      <div
        className={cn(
          'flex items-center shrink-0 h-11 bg-card',
          collapsed && 'border-t border-border',
        )}
      >
        {/* THE TAB ROW AND THE TABLE UNDER IT SHARE ONE CONTENT EDGE. The two are stacked and read
            as one block, so the eye expects "Open trades" to start where "Ticket" starts — and it
            did not, by 14px: the strip carried px-4 (16) and each tab is a pill carrying px-2.5
            (10) of its own, while the table's th carries px-3 (12). Reconciled rather than nudged:
            the strip takes pl-3 to match the table's token exactly, and the first tab takes
            -ml-2.5 to negate its own pill padding exactly. Both numbers ARE the token they cancel,
            so neither drifts if the other is retuned. pr-4 is left alone — only the left edge is
            in question. Known consequence: the active pill's background now starts 2px off the
            pane edge, because a pill wrapping 10px around text that must sit at 12 cannot also be
            inset. */}
        <div className="flex-1 flex items-center gap-0.5 min-w-0 pl-3 pr-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map(({ id, label, Icon, count }, i) => {
            const active = tab === id
            return (
              <button
                key={id}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setTab(id)
                  setCollapsed(false)
                }}
                className={cn(
                  'shrink-0 flex items-center gap-1.5 py-1 px-2.5 rounded-md text-caption transition-colors cursor-pointer',
                  i === 0 && '-ml-2.5',
                  active
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                <Icon size={11} strokeWidth={1.5} />
                <span>{label}</span>
                <span className={cn('tabular-nums', count > 0 ? 'opacity-100' : 'opacity-70')}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand trades' : 'Collapse trades'}
          aria-label={collapsed ? 'Expand trades' : 'Collapse trades'}
          className="shrink-0 mr-4 inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
        >
          {collapsed ? <ChevronUp size={12} strokeWidth={2} /> : <ChevronDown size={12} strokeWidth={2} />}
        </button>
      </div>

      {!collapsed && (
        <div style={{ height }} className="min-h-0 overflow-y-auto border-t border-border">
          {rows.length === 0 ? (
            <div className="flex items-center justify-center h-full px-4 text-center text-caption text-muted-foreground">
              {tab === 'open'
                ? 'No open trades at this point in the replay.'
                : 'No closed trades yet — press play to watch your strategy trade.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* A floor on a phone so the eight columns scroll sideways rather than crush
                  into unreadable slivers. Harmless above it — `w-full` already exceeds it. */}
              <table className="w-full min-w-[44rem] border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    {COLUMNS.map((c) => (
                      <th
                        key={c}
                        className="px-3 py-1.5 text-left text-caption text-muted-foreground font-medium whitespace-nowrap"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <TradeRows
                    rows={rows}
                    precision={precision}
                    onShowTrade={onShowTrade}
                    selectedTicket={selectedTicket}
                    onSelect={setSelectedTicket}
                  />
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
